import React from "react";
import { Modal } from "antd";
import { ExternalCandidate } from "../../../services/api/externalCandidate";
import { X } from "lucide-react";

interface CandidateDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  candidate: ExternalCandidate | null;
}

const CandidateDetailsModal: React.FC<CandidateDetailsModalProps> = ({
  isOpen,
  onClose,
  candidate,
}) => {
  if (!candidate) return null;

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={900}
      closeIcon={<X size={20} />}
    >
      <div className="font-abel">
        {/* Header */}
        <div className="mb-6 pb-4 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 capitalize">
            {candidate.firstName} {candidate.lastName}
          </h2>
          <p className="text-lg text-gray-600 mt-1">
            {candidate.professionalTitle || "N/A"}
          </p>
        </div>

        {/* Contact & Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Contact Information
            </h3>
            <div className="space-y-2">
              <p className="text-gray-800">
                <span className="font-medium">Email:</span> {candidate.email}
              </p>
              {(candidate as any).phone && (
                <p className="text-gray-800">
                  <span className="font-medium">Phone:</span>{" "}
                  {(candidate as any).phone}
                </p>
              )}
              <p className="text-gray-800">
                <span className="font-medium">Location:</span>{" "}
                {candidate.location?.city && `${candidate.location.city}, `}
                {candidate.location?.state && `${candidate.location.state}, `}
                {candidate.location?.country || "N/A"}
              </p>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Professional Details
            </h3>
            <div className="space-y-2">
              <p className="text-gray-800">
                <span className="font-medium">Experience:</span>{" "}
                {candidate.yearsOfExperience || 0} years
              </p>
              {candidate.overallRating && (
                <p className="text-gray-800">
                  <span className="font-medium">Overall Rating:</span>{" "}
                  <span
                    className={`px-2 py-1 rounded text-sm ${
                      candidate.overallRating >= 4.0
                        ? "bg-blue-500 text-white"
                        : candidate.overallRating >= 3.0
                          ? "bg-blue-500 text-white"
                          : "bg-gray-200"
                    }`}
                  >
                    {candidate.overallRating.toFixed(1)}
                  </span>
                </p>
              )}
              {candidate.cvJobMatchRating && (
                <p className="text-gray-800">
                  <span className="font-medium">CV Match:</span>{" "}
                  {candidate.cvJobMatchRating.toFixed(1)}/5.0
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Summary */}
        {(candidate as any).summary && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Professional Summary
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {(candidate as any).summary}
            </p>
          </div>
        )}

        {/* Skills */}
        {candidate.skills && candidate.skills.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Skills ({candidate.skills.length})
            </h3>
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((skill, index) => (
                <span
                  key={index}
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    skill.level === "EXPERT"
                      ? "bg-purple-100 text-purple-800"
                      : skill.level === "ADVANCED"
                        ? "bg-blue-100 text-blue-800"
                        : skill.level === "INTERMEDIATE"
                          ? "bg-blue-100 text-blue-800"
                          : "bg-gray-100 text-gray-800"
                  }`}
                >
                  {skill.skillName}
                  {skill.level && ` (${skill.level})`}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Education */}
        {candidate.highestEducation && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Education
            </h3>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-medium text-gray-900">
                {candidate.highestEducation.degree || "N/A"}
              </p>
              <p className="text-gray-600 text-sm">
                {candidate.highestEducation.institution} •{" "}
                {candidate.highestEducation.level}
              </p>
              {(candidate.highestEducation as any).fieldOfStudy && (
                <p className="text-gray-600 text-sm">
                  Field: {(candidate.highestEducation as any).fieldOfStudy}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Certifications */}
        {candidate.certifications && candidate.certifications.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Certifications
            </h3>
            <div className="space-y-3">
              {candidate.certifications.map((cert, index) => (
                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-gray-900">{cert.name}</p>
                  <p className="text-gray-600 text-sm">
                    {cert.issuingOrganization}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Expected Salary & Work Preferences */}
        {candidate.expectedSalaryRange && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-2">
              Salary Expectations
            </h3>
            <p className="text-gray-800">
              {candidate.expectedSalaryRange.currency}{" "}
              {candidate.expectedSalaryRange.min?.toLocaleString()} -
              {candidate.expectedSalaryRange.max?.toLocaleString()}
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CandidateDetailsModal;
